import { Tooltip } from '@mui/material';
import { useCallback, useState } from 'react';
import { HiOutlineExternalLink } from 'react-icons/hi';
import { Button } from './ui/button';
import { Clipboard, ClipboardCheck } from 'lucide-react';
import { Label } from './ui/label';
import { CustomTooltip } from './CustomTooltip';
import { Info } from 'lucide-react';

type CopyLinkFieldProps = {
  label: string;
  url: string;
  tooltipContent?: React.ReactNode;
  showOpenButton?: boolean;
  className?: string;
};

export function CopyLinkField({
  label,
  url,
  tooltipContent,
  showOpenButton = true,
  className,
}: CopyLinkFieldProps) {
  const [showCheckmark, setShowCheckmark] = useState(false);
  const [tooltipText, setTooltipText] = useState('Click to copy');

  function handleOpenLink() {
    window.open(url, '_blank');
  }

  const copyLinkToClipboard = useCallback(() => {
    navigator.clipboard.writeText(url);
    setShowCheckmark(true);
    setTooltipText('Copied!');
    setTimeout(() => {
      setShowCheckmark(false);
      setTooltipText('Click to copy');
    }, 4000);
  }, [url]);

  return (
    <div className={className}>
      <div className="mb-2 flex items-center gap-2">
        <Label className="text-sm font-medium text-primary-dark">{label}</Label>
        {tooltipContent && (
          <CustomTooltip
            content={tooltipContent}
            side="right"
            align="end"
          >
            <Info className="h-4 w-4 text-primary-500" />
          </CustomTooltip>
        )}
      </div>
      <div className="mb-4 mt-2 flex h-10 items-center justify-between rounded-md border border-gray-100 bg-primary-50 px-2 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-primary">
        <Tooltip title={<p className="text-sm">{tooltipText}</p>}>
          <p
            className="mr-2 flex max-w-60 cursor-pointer select-none items-center gap-1 truncate text-gray-700 hover:text-gray-900 hover:underline md:max-w-sm"
            onClick={copyLinkToClipboard}
            aria-label="Click to copy link"
          >
            {url}
          </p>
        </Tooltip>
        <div className="ml-1 flex shrink-0 items-center gap-1">
          <Tooltip title={<p className="text-sm">{tooltipText}</p>}>
            <Button
              onClick={copyLinkToClipboard}
              variant="ghost"
              size="sm"
              className="h-8 px-2 hover:bg-primary-200"
            >
              {showCheckmark ? (
                <ClipboardCheck className="h-4 w-4 text-green-600" />
              ) : (
                <Clipboard className="h-4 w-4 text-primary-hover" />
              )}
            </Button>
          </Tooltip>
          {showOpenButton && (
            <Tooltip title={<p className="my-auto text-sm">Open</p>}>
              <Button
                onClick={handleOpenLink}
                variant="ghost"
                size="sm"
                className="h-8 px-2 hover:bg-primary-200"
              >
                <HiOutlineExternalLink className="h-5 w-5 text-primary-hover" />
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
